import { all, takeLatest, call, put } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR, SUCCESS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { saveToLocalStorage } from '../../utils/util';

import { requestLogin, requestResetPassword, requestSendResetPasswordEmail } from './login.actions';
import { requestViewPersonProfile } from '../party/party.actions';
import { requestRetrieveShortcuts } from '../system-user/system-user.actions';
import { getLoginRequest, getResetPasswordRequest, getSendResetPasswordEmailRequest } from './login.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setAdminRole, setUserGroups } from '../../slices/party/party.slice';

export function* performLoginRequest({ payload }) {
  const { input, deleteAccount, onSuccess, navigate } = payload;

  try {
    yield put(addSpinner('Logging In'));

    const [endpoint, options] = getLoginRequest(input);

    const { data } = yield call(axios, endpoint, options);

    const { token, user, registrationStatus, keyUUID } = data;

    if (deleteAccount) {
      yield call(saveToLocalStorage, 'token', token);
      onSuccess(user.personExternalReference);
      return;
    }

    if (registrationStatus === 'Registration Incomplete') {
      yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
      yield put(removeSpinner('Logging In'));
      if (keyUUID) {
        yield call(navigate, `/registration/${user?.personExternalReference}?keyExternalReference=${keyUUID}`);
      } else {
        yield call(navigate, `/registration/${user?.personExternalReference}`);
      }
      return;
    } else if (registrationStatus === 'Pending Email Verification') {
      const message = 'Please check your emails, and verify your email address.';
      yield put(removeSpinner('Logging In'));
      yield put(addSystemNotice({ type: ERROR, message }));
      yield call(navigate, '/');
    }

    if (user && user.belongsToGroups && user.belongsToGroups.length > 0) {
      if (user.belongsToGroups.includes('admin')) {
        yield put(setAdminRole(true));
      }
    }

    yield put(setUserGroups(user.belongsToGroups));

    yield call(saveToLocalStorage, 'token', token);
    yield call(saveToLocalStorage, 'personExternalReference', user.personExternalReference);
    yield call(saveToLocalStorage, 'preferences', user.preferences);

    yield put(requestViewPersonProfile({ personExternalReference: user.personExternalReference }));
    yield put(requestRetrieveShortcuts());

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Logging In'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Logging In'));
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Logging In'));
  }
}

export function* watchForLoginRequest() {
  yield takeLatest(requestLogin.type, performLoginRequest);
}

export function* performSendResetPasswordEmailRequest({ payload }) {
  const { email, onSuccess } = payload;

  try {
    yield put(addSpinner('Sending reset password email'));

    const [endpoint, options] = getSendResetPasswordEmailRequest(email);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Sending reset password email'));
  }
}

export function* watchForSendResetPasswordEmailRequest() {
  yield takeLatest(requestSendResetPasswordEmail.type, performSendResetPasswordEmailRequest);
}

export function* performResetPasswordRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating password'));

    const [endpoint, options] = getResetPasswordRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating password'));
  }
}

export function* watchForResetPasswordRequest() {
  yield takeLatest(requestResetPassword.type, performResetPasswordRequest);
}

export default function* loginSaga() {
  yield all([watchForLoginRequest(), watchForSendResetPasswordEmailRequest(), watchForResetPasswordRequest()]);
}
